import React, { useEffect, useMemo, useRef, useState } from 'react';
import { playChime } from '../utils/notify';
import { shouldChimeNewEdgeMessage } from '../utils/liveChatChime';

const LIVE_CHAT_STATUS_META = {
  online: {
    statusWord: 'Live',
    dot: 'bg-green-500',
    pill: 'bg-green-50 text-green-800 border-green-200',
  },
  busy: {
    statusWord: 'Busy',
    dot: 'bg-yellow-500',
    pill: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  },
  offline: {
    statusWord: 'Offline',
    dot: 'bg-gray-500',
    pill: 'bg-gray-50 text-gray-800 border-gray-200',
  },
};

/**
 * @param {{ messages: unknown[], session: { id?: string } | null, isLoading: boolean, refresh: () => Promise<void> } | null} sharedChat - When set, polling is owned by the parent (single source of truth).
 */
export default function LiveChatPanel({
  selectedEmployeeId,
  employees,
  onSelectEmployee,
  supportStatus,
  documents = [],
  onOpenSopDocument = null,
  showStatusBadge = true,
  // When embedded inside a surface that already communicates the
  // "busy" state (e.g., SupportDrawer's primary banner), set this to
  // suppress the panel's own amber banner to avoid saying the same
  // thing twice. LiveChatRail shows no outer banner, so it omits
  // this prop and the banner remains its only indicator.
  suppressBusyBanner = false,
  sharedChat = null,
}) {
  const [messages, setMessages] = useState([]);
  const [, setSessionId] = useState(null);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sendError, setSendError] = useState('');
  const messagesEndRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const pollRef = useRef(null);
  const hasScrolledRef = useRef(false);

  const controlled = sharedChat != null;
  const liveChatAvailable =
    supportStatus === 'online' || supportStatus === 'busy';

  const mode = supportStatus || 'offline';
  const statusMeta = LIVE_CHAT_STATUS_META[mode] || LIVE_CHAT_STATUS_META.offline;

  const displayMessages = useMemo(() => {
    if (sharedChat != null) {
      return Array.isArray(sharedChat.messages) ? sharedChat.messages : [];
    }
    return messages;
  }, [sharedChat, messages]);

  const displayLoading = useMemo(() => {
    if (sharedChat != null) return !!sharedChat.isLoading;
    return isLoading;
  }, [sharedChat, isLoading]);

  useEffect(() => {
    if (controlled) return undefined;

    let alive = true;

    const fetchSession = async () => {
      try {
        const res = await fetch('/api/support/live-chat/session');
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j && j.ok) {
          setSessionId(j.session ? j.session.id : null);
          const newMessages = Array.isArray(j.messages) ? j.messages : [];
          const prevLen = prevMessageCountRef.current;
          if (shouldChimeNewEdgeMessage(prevLen, newMessages, 'jonathan')) {
            try {
              playChime();
            } catch {
              /* ignore */
            }
          }
          prevMessageCountRef.current = newMessages.length;
          setMessages(newMessages);
          setIsLoading(false);
        }
      } catch {
        if (alive) setIsLoading(false);
      }
    };

    fetchSession();
    pollRef.current = setInterval(fetchSession, document.hidden ? 30000 : 4000);

    const onVisibility = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchSession, document.hidden ? 30000 : 4000);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [controlled]);

  useEffect(() => {
    if (!messagesEndRef.current) return;
    const behavior = hasScrolledRef.current ? 'smooth' : 'instant';
    hasScrolledRef.current = true;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior });
    });
  }, [displayMessages]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    const emp = (employees || []).find((e) => e.id === selectedEmployeeId);
    if (!emp) {
      setSendError('Please select your name before chatting.');
      return;
    }

    setSendError('');
    setIsSending(true);
    setInputText('');

    try {
      const res = await fetch('/api/support/live-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderType: 'fd',
          senderName: emp.name,
          text,
        }),
      });
      const j = await res.json();
      if (!res.ok || !j.ok) {
        setSendError('Failed to send. Please try again.');
        setInputText(text);
      } else if (controlled && typeof sharedChat.refresh === 'function') {
        await sharedChat.refresh();
      }
    } catch {
      setSendError('Network error. Please try again.');
      setInputText(text);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 280 }}>

      {showStatusBadge && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusMeta.dot}`} aria-hidden />
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusMeta.pill}`}
          >
            Status: {statusMeta.statusWord}
          </span>
        </div>
      )}

      {!selectedEmployeeId && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <p className="font-semibold mb-2">Select your name to start chatting:</p>
          <select
            className="w-full p-2 border rounded-md bg-white text-gray-700"
            value={selectedEmployeeId || ''}
            onChange={(e) => onSelectEmployee(e.target.value)}
          >
            <option value="">Select your name…</option>
            {(employees || []).map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>
      )}

      {supportStatus === 'offline' && (
        <div className="mb-3 p-3 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 text-center">
          Jonathan is offline. This session has ended.
        </div>
      )}

      {supportStatus === 'busy' && !suppressBusyBanner && (
        <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          Live Chat available but please expect delays.
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2 mb-3"
        style={{ maxHeight: 300, minHeight: 200 }}
      >
        {displayLoading ? (
          <div className="text-center text-gray-400 text-sm py-4">Loading...</div>
        ) : displayMessages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-4">
            No messages yet. Say hi to Jonathan! 👋
          </div>
        ) : (
          displayMessages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.senderType === 'fd' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  msg.senderType === 'fd'
                    ? 'bg-teal-600 text-white rounded-br-sm'
                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                }`}
              >
                <div className="font-semibold text-xs mb-0.5 opacity-70">
                  {msg.senderName}
                </div>
                {!!String(msg.text || '').trim() && (
                  <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                )}
                {msg.sopDocId && (
                  <button
                    type="button"
                    onClick={() => {
                      const sopDoc = (Array.isArray(documents) ? documents : []).find((d) => d.id === msg.sopDocId);
                      if (sopDoc && typeof onOpenSopDocument === 'function') onOpenSopDocument(sopDoc);
                    }}
                    className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                      msg.senderType === 'fd'
                        ? 'bg-white text-teal-700 border-white hover:bg-teal-50'
                        : 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50'
                    }`}
                    title="Open SOP"
                  >
                    <span aria-hidden>📎</span>
                    <span>{msg.sopName || 'Open SOP'}</span>
                  </button>
                )}
                <div className="text-[10px] mt-1 opacity-60 text-right">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {sendError && (
        <div className="text-red-600 text-xs mb-2 px-1">{sendError}</div>
      )}

      <div className="flex gap-2">
        <textarea
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
          rows={2}
          placeholder={
            !selectedEmployeeId
              ? 'Select your name above first…'
              : !liveChatAvailable
                ? 'Session ended'
                : 'Type a message… (Enter to send)'
          }
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!selectedEmployeeId || !liveChatAvailable || isSending}
          maxLength={2000}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!selectedEmployeeId || !inputText.trim() || !liveChatAvailable || isSending}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors self-end"
        >
          {isSending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
